/**
 * Manus Quick Wins: /resources/manus/quick-wins
 *
 * The ten Quick Wins from Caren's Manus PDF. Same component shape as the
 * Gemini Notebook, Littlebird and Perplexity Quick Wins pages, so the set
 * reads as one series rather than four attempts at the same idea.
 *
 * The wins are the PDF's, close to verbatim: they are the deliverable, and
 * rewriting them here would put two versions of Caren's words in circulation
 * with no way to tell which was current.
 */

import { useState } from "react";
import GuideShell from "@/components/GuideShell";
import { Link } from "wouter";

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  cream: "oklch(0.98 0.008 85)",
  linen: "oklch(0.93 0.01 85)",
  espresso: "oklch(0.22 0.015 65)",
  terracotta: "oklch(0.32 0.08 255)",
  terracottaLight: "oklch(0.32 0.08 255 / 0.08)",
  terracottaMid: "oklch(0.32 0.08 255 / 0.22)",
  border: "oklch(0.88 0.012 85)",
  white: "oklch(1 0.004 85)",
  muted: "oklch(0.52 0.015 65)",
  body: "oklch(0.52 0.015 65)",
};

// ── Data ─────────────────────────────────────────────────────────────────────
interface QuickWin {
  num: number;
  title: string;
  when: string;
  how: string[];
  principle: string;
}

const WINS: QuickWin[] = [
  {
    num: 1,
    title: "Define the Goal, Then Step Back",
    when: "Use this when a task is clear enough to hand off.",
    how: [
      "Write a single-sentence goal, not a process",
      "Include the output format you expect",
      "Set any constraints upfront (length, tone, sources)",
      "Let Manus determine the steps",
    ],
    principle: "A clear goal is better than a detailed process.",
  },
  {
    num: 2,
    title: "Use Manus for Research-Intensive Tasks",
    when: "Use this when gathering and synthesizing information takes too long.",
    how: [
      "Give Manus a research question, not a search term",
      "Specify the type of output: summary, report, comparison",
      "Let it browse, compile, and synthesize across sources",
      "Review the output, don't redo the research",
    ],
    principle: "Let the agent do the gathering. You do the deciding.",
  },
  {
    num: 3,
    title: "Build and Launch a Web App",
    when: "Use this when you need a working app but don't have a developer.",
    how: [
      "Describe the app: what it does, who uses it, what it should look like",
      "Let Manus write the code, build the interface, and deploy it",
      "No coding required: review the working version, not the code",
      "Use for tools, calculators, lead magnets, or internal utilities",
    ],
    principle: "A working app is a finished thought.",
  },
  {
    num: 4,
    title: "Automate Repetitive Document Work",
    when: "Use this when a task follows a predictable pattern.",
    how: [
      "Identify tasks you do the same way every time",
      "Give Manus one example or a clear template",
      "Let it replicate the pattern across your inputs",
      "Reserve your attention for decisions, not repetition",
    ],
    principle: "Pattern recognition belongs to the agent.",
  },
  {
    num: 5,
    title: "Create Deliverables Ready to Use",
    when: "Use this when you need a finished file, not a draft.",
    how: [
      "Specify the format: PDF, spreadsheet, slide deck, webpage",
      "Describe the content and structure clearly",
      "Let Manus build and package the output",
      "Review and ship, without reformatting",
    ],
    principle: "The output should be the work, not the start of it.",
  },
  {
    num: 6,
    title: "Run a Multi-Step Workflow Unattended",
    when: "Use this when a task has several sequential steps.",
    how: [
      "Describe the full workflow from start to finish",
      "Manus will move through each step without prompting",
      "Check the output at the end, not between steps",
      "Use this for processes you'd otherwise interrupt yourself to manage",
    ],
    principle: "Autonomous means it doesn't need you watching.",
  },
  {
    num: 7,
    title: "Pair Manus With Your Thinking Tool",
    when: "Use this when you need both reasoning and execution.",
    how: [
      "Use a reasoning tool to think through strategy or structure",
      "Hand the execution task to Manus once the thinking is done",
      "Keep the roles separate: one reasons, one builds",
      "Bring the finished output back for review",
    ],
    principle: "Thinking and doing are different jobs.",
  },
  {
    num: 8,
    title: "Use Manus to Prep Before a Meeting or Session",
    when: "Use this when preparation pulls you away from higher-value work.",
    how: [
      "Give Manus the topic, context, and desired output",
      "Ask for background research, talking points, or a one-pager",
      "Let it assemble the material before you arrive",
      "You show up prepared, without doing the prep",
    ],
    principle: "Preparation shouldn't cost you thinking time.",
  },
  {
    num: 9,
    title: "Analyze Data and Visualize It",
    when: "Use this when a spreadsheet has answers you haven't found yet.",
    how: [
      "Upload your data file and describe what you want to understand",
      "Ask for patterns, comparisons, or summaries, not just totals",
      "Request charts or visual outputs ready to share or present",
      "Review the insights, don't redo the analysis",
    ],
    principle: "Data doesn't speak. A good question makes it.",
  },
  {
    num: 10,
    title: "Let Manus Handle the Friction Work",
    when: "Use this when logistics and admin are pulling you out of focus.",
    how: [
      "Identify tasks that require effort but not judgment",
      "Delegate formatting, organizing, compiling, and structuring",
      "Manus handles the friction; you handle the thinking",
      "Protect your attention for the work only you can do",
    ],
    principle: "Friction work is a cost. Delegate it.",
  },
];

const GROUPS = [
  { label: "Quick Wins 1 – 3", wins: WINS.slice(0, 3) },
  { label: "Quick Wins 4 – 6", wins: WINS.slice(3, 6) },
  { label: "Quick Wins 7 – 10", wins: WINS.slice(6, 10) },
];

// ── Chevron icon ──────────────────────────────────────────────────────────────
function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="16" height="16" viewBox="0 0 16 16" fill="none"
      style={{
        transition: "transform 0.25s ease",
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
        flexShrink: 0,
        color: C.muted,
      }}
    >
      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Win card ──────────────────────────────────────────────────────────────────
function WinCard({ win, defaultOpen = false }: { win: QuickWin; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{
        background: C.white,
        border: `1px solid ${open ? C.terracottaMid : C.border}`,
        borderLeft: `3px solid ${open ? C.terracotta : C.terracottaMid}`,
        borderRadius: 10,
        overflow: "hidden",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        boxShadow: open
          ? "0 6px 24px oklch(0.32 0.08 255 / 0.10)"
          : hovered
          ? "0 2px 10px rgba(0,0,0,0.06)"
          : "0 1px 4px rgba(0,0,0,0.04)",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          padding: "1.1rem 1.4rem",
          background: open ? C.terracottaLight : "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          transition: "background 0.2s ease",
        }}
      >
        <span
          style={{
            width: 32, height: 32, minWidth: 32,
            borderRadius: "50%",
            background: open ? C.terracotta : C.terracottaLight,
            color: open ? C.white : C.terracotta,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: 13, fontWeight: 700,
            transition: "background 0.2s ease, color 0.2s ease",
            flexShrink: 0,
          }}
        >
          {win.num}
        </span>
        <span
          style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: 16, fontWeight: 600, color: C.espresso,
            flex: 1, lineHeight: 1.35,
          }}
        >
          {win.title}
        </span>
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div
          style={{
            padding: "1.25rem 1.4rem 1.5rem",
            borderTop: `1px solid ${C.terracottaMid}`,
            background: C.white,
          }}
        >
          <div style={{ marginBottom: "1.1rem" }}>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color: C.terracotta, marginBottom: "0.35rem" }}>
              When This Helps
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.muted, fontStyle: "italic", lineHeight: 1.7 }}>
              {win.when}
            </p>
          </div>

          <div style={{ marginBottom: "1.25rem" }}>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color: C.terracotta, marginBottom: "0.65rem" }}>
              How
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
              {win.how.map((step, i) => (
                <div key={i} style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start" }}>
                  <span
                    style={{
                      width: 20, height: 20, minWidth: 20,
                      borderRadius: "50%",
                      background: C.linen,
                      border: `1px solid ${C.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                      fontSize: 10, fontWeight: 700, color: C.muted,
                      marginTop: 2, flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </span>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.body, lineHeight: 1.7, margin: 0 }}>
                    {step}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              background: C.terracottaLight,
              border: `1px solid ${C.terracottaMid}`,
              borderLeft: `3px solid ${C.terracotta}`,
              borderRadius: 6,
              padding: "0.75rem 1rem",
            }}
          >
            <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color: C.terracotta, display: "block", marginBottom: "0.3rem" }}>
              Smart Stack Principle
            </span>
            <span style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 14, color: C.espresso, fontStyle: "italic", lineHeight: 1.5 }}>
              {win.principle}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function ManusQuickWinsPage() {
  return (
    <GuideShell
      context={"Manus guide"}
      backHref={"/resources/manus"}
      backLabel={"Back to Manus"}
    >

      <main style={{ maxWidth: 720, margin: "0 auto", padding: "3.5rem 1.5rem 5rem" }}>

        {/* Hero header */}
        <div style={{ textAlign: "center", marginBottom: "3rem", paddingBottom: "2.5rem", borderBottom: `1px solid ${C.border}` }}>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 11, fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: C.terracotta,
              marginBottom: "1rem",
            }}
          >
            Your Smart Stack Quick Wins
          </p>
          <h1
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: "clamp(28px, 5vw, 40px)",
              fontWeight: 700,
              color: C.espresso,
              lineHeight: 1.2,
              marginBottom: "0.75rem",
            }}
          >
            Manus
          </h1>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 18,
              color: C.muted,
              fontStyle: "italic",
              marginBottom: "1.5rem",
              lineHeight: 1.5,
            }}
          >
            Delegate the doing. Stay in thinking mode.
          </p>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              background: C.terracottaLight,
              border: `1px solid ${C.terracottaMid}`,
              borderRadius: 20,
              padding: "0.45rem 1.1rem",
            }}
          >
            <span
              style={{
                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                fontSize: 12, fontWeight: 600,
                color: C.terracotta,
                letterSpacing: "0.04em",
              }}
            >
              Small moves. Clear thinking. Less friction.
            </span>
          </div>
        </div>

        {/* Intro card */}
        <div
          style={{
            background: C.white,
            border: `1px solid ${C.border}`,
            borderLeft: `3px solid ${C.terracotta}`,
            borderRadius: 10,
            padding: "1.75rem 2rem",
            marginBottom: "2.75rem",
          }}
        >
          <h2 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, fontWeight: 700, color: C.espresso, marginBottom: "0.3rem" }}>
            Build and ship, without leaving your thinking space.
          </h2>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.muted, fontStyle: "italic", marginBottom: "1.1rem" }}>
            Manus doesn't wait for direction. It acts.
          </p>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.body, lineHeight: 1.8, marginBottom: "0.9rem" }}>
            Manus is an autonomous AI agent that takes a goal and works through it, researching, writing, building, and delivering, without you managing each step. Unlike a chat tool that waits for prompts, Manus operates in the background, completing multi-step tasks while you stay focused on higher-level thinking.
          </p>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.body, lineHeight: 1.8, marginBottom: "1.5rem" }}>
            This guide shows how to use Manus as your execution layer, paired with a reasoning tool, so you stay in thinking mode while the doing gets done.
          </p>

          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: "1.25rem" }}>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color: C.terracotta, marginBottom: "0.6rem" }}>
              How to Use This Guide
            </p>
            <p style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 16, fontWeight: 600, color: C.espresso, marginBottom: "0.5rem" }}>
              This is not a checklist.
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.body, lineHeight: 1.75, marginBottom: "0.75rem" }}>
              You don’t need to apply everything here. Each Quick Win is designed for moments when:
            </p>
            {[
              "a task is clear but time-consuming",
              "you need a deliverable, not a draft",
              "the doing is slowing the thinking",
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: "0.65rem", alignItems: "flex-start", marginBottom: "0.45rem" }}>
                <span style={{ color: C.terracotta, fontWeight: 700, fontSize: 15, flexShrink: 0, lineHeight: 1.6 }}>–</span>
                <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.body, lineHeight: 1.7, margin: 0 }}>{item}</p>
              </div>
            ))}
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.body, lineHeight: 1.75, marginTop: "0.85rem" }}>
              Use the one that moves something forward. Ignore the rest.
            </p>
            <p style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 15, fontWeight: 600, color: C.espresso, marginTop: "0.6rem", fontStyle: "italic" }}>
              Execution is a tool. Thinking is the work.
            </p>
          </div>
        </div>

        {/* Quick Win groups */}
        {GROUPS.map((group) => (
          <div key={group.label} style={{ marginBottom: "2.75rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.1rem" }}>
              <div style={{ height: 1, flex: 1, background: C.border }} />
              <p
                style={{
                  fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                  fontSize: 10, letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  color: C.terracotta,
                  whiteSpace: "nowrap",
                }}
              >
                {group.label}
              </p>
              <div style={{ height: 1, flex: 1, background: C.border }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
              {group.wins.map((win, i) => (
                <WinCard
                  key={win.num}
                  win={win}
                  defaultOpen={i === 0 && group.wins[0].num === 1}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Closing note */}
        <div
          style={{
            background: C.white,
            border: `1px solid ${C.border}`,
            borderLeft: `3px solid ${C.terracotta}`,
            borderRadius: 10,
            padding: "2rem 2.25rem",
            marginBottom: "2.5rem",
          }}
        >
          <h2 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, fontWeight: 700, color: C.espresso, marginBottom: "0.5rem" }}>
            A Note on Execution
          </h2>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.muted, fontStyle: "italic", marginBottom: "1.1rem", lineHeight: 1.7 }}>
            Manus works best when you've already done the thinking.
          </p>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.body, lineHeight: 1.8, marginBottom: "0.5rem" }}>
            It’s not the tool you open to figure something out.
          </p>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.body, lineHeight: 1.8, marginBottom: "1.25rem" }}>
            It’s the tool you open when you know what needs to happen and you want it handled.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginBottom: "1.75rem" }}>
            {["Define the goal.", "Delegate the doing.", "Stay in thinking mode."].map((line) => (
              <p
                key={line}
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: 16, fontWeight: 600,
                  color: C.espresso,
                  margin: 0,
                  fontStyle: "italic",
                }}
              >
                {line}
              </p>
            ))}
          </div>
          <div
            style={{
              background: C.terracottaLight,
              border: `1px solid ${C.terracottaMid}`,
              borderRadius: 8,
              padding: "1.1rem 1.4rem",
            }}
          >
            <p style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 15, color: C.espresso, fontStyle: "italic", lineHeight: 1.65, marginBottom: "0.6rem" }}>
              “Manus is where I send the work I’ve already thought through.”
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 13, fontWeight: 700, color: C.terracotta, marginBottom: "0.1rem" }}>
              Your Smart Stack
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 11, color: C.muted, letterSpacing: "0.04em" }}>
              Your Smart Stack
            </p>
          </div>
        </div>

        {/* Footer nav */}
        <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
          <Link
            href="/resources/manus"
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 13, color: C.muted,
              fontWeight: 600,
              textDecoration: "none",
              padding: "0.65rem 1.3rem",
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              background: C.white,
              transition: "border-color 0.2s ease, color 0.2s ease",
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.currentTarget.style.borderColor = C.terracotta;
              e.currentTarget.style.color = C.terracotta;
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.currentTarget.style.borderColor = C.border;
              e.currentTarget.style.color = C.muted;
            }}
          >
            ← Manus Resources
          </Link>
          <a
            href="https://manus.im/invitation/TDNFQJ04TL7FG"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 13, color: C.white,
              fontWeight: 700,
              textDecoration: "none",
              padding: "0.65rem 1.3rem",
              background: C.terracotta,
              borderRadius: 8,
              letterSpacing: "0.02em",
              transition: "opacity 0.2s ease",
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) =>
              (e.currentTarget.style.opacity = "0.85")
            }
            onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) =>
              (e.currentTarget.style.opacity = "1")
            }
          >
            Try Manus →
          </a>
        </div>
      </main>


      <style>{`
        @media (max-width: 600px) {
          main { padding: 2rem 1rem 4rem !important; }
        }
      `}</style>
    </GuideShell>
  );
}
